export const CONSULT_NOTE_SECTION_ORDER = [
  "cc",
  "hpi",
  "pmh",
  "psh",
  "family_history",
  "ros",
  "medications",
  "allergies",
  "vitals",
  "physical_exam",
  "assessment",
  "plan",
  "medical_necessity",
] as const;

export type ConsultNoteSectionKey = (typeof CONSULT_NOTE_SECTION_ORDER)[number];
export type ConsultNoteTextSectionKey = Exclude<ConsultNoteSectionKey, "assessment" | "physical_exam">;

export type ConsultNoteFlash = {
  notice?: string;
  alert?: string;
};

export type ConsultNotePendingAction =
  | "save"
  | "duplicate"
  | "regenerate"
  | "sign"
  | "extractMissingLabs"
  | "saveAddendum"
  | "signAddendum"
  | "retryTebraExport";

export type ConsultNoteMissingLabExtractionWarning = {
  message: string;
  extractAllUrl: string;
  documents: Array<{
    id: number;
    title: string;
    openUrl: string;
  }>;
};

export type ConsultNoteMultipleIntakesWarning = {
  submissions: Array<{
    id: number;
    submittedAtLabel: string;
    openUrl: string;
    usedForNote: boolean;
  }>;
};

export type ConsultNoteSignedAddendum = {
  id: number;
  body: string;
  signedAtLabel: string;
  signedByName: string;
};

export type ConsultNoteSourceRef = {
  value: string;
  label: string;
  openUrl?: string | null;
};

export type ConsultNoteVisit = {
  date: string;
  time: string;
};

export type ConsultNoteImagingRow = {
  date: string;
  imageType: string;
  results: string;
  openUrl?: string | null;
};

export type ConsultNoteTextSection = {
  key: ConsultNoteTextSectionKey;
  title: string;
  kind: "text";
  rows: number;
  editedText: string;
  generatedText: string;
  effectiveText: string;
  sourceRefs: ConsultNoteSourceRef[];
  imagingRows: ConsultNoteImagingRow[];
};

export type ConsultNoteAssessmentOption = {
  key: string;
  label: string;
  selected: boolean;
  planTemplate: string;
};

export type ConsultNoteAssessmentSection = {
  key: "assessment";
  title: string;
  kind: "assessment";
  generatedText: string;
  customText: string;
  options: ConsultNoteAssessmentOption[];
  sourceRefs: ConsultNoteSourceRef[];
};

export type ConsultNotePhysicalExamSection = {
  key: "physical_exam";
  title: string;
  kind: "physical_exam";
  editedText: string;
  generatedText: string;
  effectiveText: string;
  defaultText: string;
  sourceRefs: ConsultNoteSourceRef[];
};

export type ConsultNoteSections = {
  cc: ConsultNoteTextSection;
  hpi: ConsultNoteTextSection;
  pmh: ConsultNoteTextSection;
  psh: ConsultNoteTextSection;
  family_history: ConsultNoteTextSection;
  ros: ConsultNoteTextSection;
  medications: ConsultNoteTextSection;
  allergies: ConsultNoteTextSection;
  vitals: ConsultNoteTextSection;
  physical_exam: ConsultNotePhysicalExamSection;
  assessment: ConsultNoteAssessmentSection;
  plan: ConsultNoteTextSection;
  medical_necessity: ConsultNoteTextSection;
};

export type ConsultNoteWorkspace = {
  authenticityToken: string;
  sectionOrder: ConsultNoteSectionKey[];
  visit: ConsultNoteVisit;
  paths: {
    load: string;
    history: string;
    compare: string;
    save: string;
    details?: string;
    regenerate: string;
    duplicate?: string;
    sign: string;
    retryTebraExport: string;
    addendum: string;
    signAddendum: string;
    previewPdf: string;
    compareAiModels?: string;
    facesheetConsultNote?: string;
    faceSheet: string;
    patient: string;
  };
  patient: {
    id: number;
    fullName: string;
  };
  signedAddendums: ConsultNoteSignedAddendum[];
  draftAddendumBody: string;
  note: {
    id: number;
    title: string;
    status: string;
    statusLabel: string;
    createdAtLabel: string;
    updatedAtLabel: string;
    versionId: number;
    versionNumber: number;
    currentVersionId?: number | null;
    currentVersion: boolean;
    latestRevisionNumber: number;
    templateVersionLabel: string;
    generationModel?: string | null;
    savedAtLabel: string;
    tebraExportStatus: string;
    tebraExportStatusLabel: string;
    tebraExportError?: string | null;
    canRetryTebraExport: boolean;
    finalized: boolean;
    finalizedAtLabel?: string | null;
    readOnly: boolean;
    canSign: boolean;
  };
  warnings?: {
    multipleConsultIntakes?: ConsultNoteMultipleIntakesWarning;
    documentHistory?: string[];
    missingLabExtraction?: ConsultNoteMissingLabExtractionWarning;
  };
  aiTrace?: {
    provider?: string;
    model?: string;
    responseId?: string;
    inputSubmissionId?: number | string;
    blankedSections?: string[];
    inputPayloadFormatted?: string;
    outputDataFormatted?: string;
    errorFormatted?: string;
  };
  sections: ConsultNoteSections;
};

export type ConsultNoteRevisionSummary = {
  id: number;
  revisionNumber: number;
  eventType: string;
  eventLabel: string;
  actorName: string;
  createdAtLabel: string;
  current: boolean;
};

export type ConsultNoteRevisionSnapshotSection = {
  key: ConsultNoteSectionKey;
  title: string;
  edited_text: string;
  generated_text: string;
  effective_text: string;
};

export type ConsultNoteRevisionSnapshot = {
  note: {
    title: string;
    status: string;
    status_label: string;
    template_name: string;
    template_version: string;
    tebra_export_status: string;
    tebra_export_status_label: string;
    finalized: boolean;
    finalized_at?: string | null;
    saved_at?: string | null;
  };
  visit: {
    date: string;
    time: string;
  };
  sections: Record<ConsultNoteSectionKey, ConsultNoteRevisionSnapshotSection>;
  preview_text: string;
};

export type ConsultNoteRevisionFieldChange = {
  field: string;
  label: string;
  from: string;
  to: string;
};

export type ConsultNoteRevisionSectionChange = {
  key: ConsultNoteSectionKey;
  title: string;
  from: string;
  to: string;
};

export type ConsultNoteRevisionDiff = {
  note: ConsultNoteRevisionFieldChange[];
  visit: ConsultNoteRevisionFieldChange[];
  sections: ConsultNoteRevisionSectionChange[];
};

export type ConsultNoteRevisionHistoryResponse = {
  revisions: ConsultNoteRevisionSummary[];
};

export type ConsultNoteRevisionCompareResponse = {
  revision: ConsultNoteRevisionSummary;
  previousRevision?: ConsultNoteRevisionSummary | null;
  snapshot: ConsultNoteRevisionSnapshot;
  diff: ConsultNoteRevisionDiff;
};

export type ConsultNoteWorkspaceBootstrap = {
  loadPath: string;
  patient: {
    id: number;
    fullName: string;
    patientPath: string;
  };
};

export type ConsultNoteDraftStatus = {
  state: "queued" | "building" | "failed";
  message: string;
  pollIntervalMs?: number;
};

export type ConsultNoteWorkspaceResponse = {
  bootstrap: ConsultNoteWorkspaceBootstrap;
  workspace?: ConsultNoteWorkspace | null;
  draftStatus?: ConsultNoteDraftStatus | null;
  flash?: ConsultNoteFlash;
};

export type ConsultNoteFormTextSection = Omit<ConsultNoteTextSection, "editedText" | "generatedText" | "effectiveText"> & {
  kind: "text";
  value: string;
};

export type ConsultNoteFormAssessmentSection = Omit<ConsultNoteAssessmentSection, "generatedText">;

export type ConsultNoteFormPhysicalExamSection = Omit<
  ConsultNotePhysicalExamSection,
  "editedText" | "generatedText" | "effectiveText"
> & {
  value: string;
};

export type ConsultNoteFormSections = {
  cc: ConsultNoteFormTextSection;
  hpi: ConsultNoteFormTextSection;
  pmh: ConsultNoteFormTextSection;
  psh: ConsultNoteFormTextSection;
  family_history: ConsultNoteFormTextSection;
  ros: ConsultNoteFormTextSection;
  medications: ConsultNoteFormTextSection;
  allergies: ConsultNoteFormTextSection;
  vitals: ConsultNoteFormTextSection;
  physical_exam: ConsultNoteFormPhysicalExamSection;
  assessment: ConsultNoteFormAssessmentSection;
  plan: ConsultNoteFormTextSection;
  medical_necessity: ConsultNoteFormTextSection;
};
